import {
  CircleNotch,
  DownloadSimple,
  type Icon,
  Paperclip,
  VideoCamera,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import LoadableImage from "common/components/ui/LoadableImage";

import type { JSX } from "react";

type FileAttachmentProps = (
  | {
      file: File;
      errorMessage: string | undefined;
    }
  | {
      fileData: {
        name: string;
        byteSize: number;
        mimeType: string;
        url?: string;
      };
    }
) & { onRemove: (() => void) | undefined };

export default function FileAttachment(props: FileAttachmentProps) {
  const { onRemove } = props;
  const isUploading = "file" in props;
  const isError = "errorMessage" in props && props.errorMessage;

  let mimeType: string, fileUrl: string, fileName: string, byteSize: number;

  if (isUploading) {
    const { file } = props;
    mimeType = file.type;
    fileUrl = URL.createObjectURL(file);
    fileName = file.name;
    byteSize = file.size;
  } else if (!isError) {
    const { fileData } = props;
    mimeType = fileData.mimeType;
    fileUrl = fileData.url;
    fileName = fileData.name;
    byteSize = fileData.byteSize;
  }

  let previewComponent: JSX.Element;

  if (mimeType.startsWith("image/")) {
    previewComponent = (
      <LoadableImage
        alt=""
        src={fileUrl}
        className="size-8 rounded-xl shrink-0"
      />
    );
  } else {
    let IconComponent: Icon;

    if (isError) {
      IconComponent = WarningCircle;
    } else if (isUploading) {
      IconComponent = CircleNotch;
    } else if (mimeType.startsWith("video/")) {
      IconComponent = VideoCamera;
    } else {
      IconComponent = Paperclip;
    }

    previewComponent = (
      <div
        className={clsx(
          "rounded-xl group flex items-center justify-center shrink-0  size-8",
          isError ? "text-red-500 bg-red-100" : "text-purple-600 bg-purple-50",
        )}
      >
        <IconComponent
          className={clsx(
            !isError && "group-hover:hidden",
            isUploading && !isError && "animate-spin",
          )}
        />
        <RenderIf condition={!isUploading}>
          <DownloadSimple size={16} className="hidden group-hover:block" />
        </RenderIf>
      </div>
    );
  }

  let subtitleText = isUploading
    ? "Uploading..."
    : `${(byteSize / 1024 / 1024).toFixed(2)} MB`;
  let subtitleClass = isUploading ? "text-purple-500" : "text-black-500";

  if (isError) {
    subtitleText = props.errorMessage;
    subtitleClass = "text-red-500";
  }

  const content = (
    <>
      {previewComponent}

      <div className="flex flex-col text-start overflow-hidden">
        <span className="truncate text-xs-bold">{fileName}</span>

        <span className={clsx("text-button-xxs", subtitleClass)}>
          {subtitleText}
        </span>
      </div>
    </>
  );

  const containerClass = clsx(
    "flex max-lg:w-full flex-row items-center gap-1.5 rounded-2xl border p-1.5 pr-4 hover:shadow-sm lg:max-w-56",
    isError ? "bg-red-50 border-red-400" : "bg-white border-black-200",
  );

  return (
    <div className="flex relative group">
      {fileUrl ? (
        <Link
          to={fileUrl}
          target="_blank"
          download={fileName}
          className={containerClass}
        >
          {content}
        </Link>
      ) : (
        <div className={containerClass}>{content}</div>
      )}
      <RenderIf condition={!!onRemove}>
        <button
          type="button"
          className="absolute right-1.5 lg:-top-1 lg:-right-1 lg:group-hover:block lg:hidden"
          onClick={onRemove}
        >
          <XCircle
            className="text-black-500 transition-colors hover:text-black-700"
            weight="fill"
          />
        </button>
      </RenderIf>
    </div>
  );
}
